"use client";

/**
 * One line of the settings sheet: what it is on the left, the control on the
 * right.
 *
 * EVERY ROW IS THE SAME OBJECT, which is the whole point of the component
 * existing rather than each setting laying itself out. The sheet's first
 * version had three groups with three internal shapes — a label over a value,
 * a paragraph above two fields, a control with a note under it — so there was
 * no left edge, no right edge, and no rhythm for the eye to follow down. A
 * settings sheet is a list, and a list only reads as one when its rows are
 * interchangeable.
 *
 * THE REASON SITS UNDER THE LABEL, not in a preamble. The handles group opened
 * with two sentences and thirty words explaining how ads are named, above the
 * fields it was talking about. Five words under each label say the same thing
 * where the person is actually looking, and no row has to be read before the
 * row it explains.
 *
 * The control column is a fixed width past `sm` so that two fields and a
 * segmented control all end on one line — that shared right edge is what makes
 * the sheet look composed rather than stacked. Below `sm` there is not room
 * for two columns, so the row stacks and the control takes the width.
 */

import type { ReactNode } from "react";
// The form Label, not the type role of the same name: this one is a Radix
// label and carries `htmlFor`, so clicking the row's text focuses its control.
import { Label } from "@/components/ui/design-system/label";
import { Hint } from "@/components/ui/design-system/text";
import { cn } from "@/lib/utils";

/**
 * The control column. Put it on anything that has to line up with its
 * neighbours — a field, a segmented control, the save button's row.
 */
export const SETTING_CONTROL = "w-full shrink-0 sm:w-[220px]";

export function SettingRow({
  label,
  hint,
  htmlFor,
  control,
}: {
  label: ReactNode;
  /** Why this exists, in a line. Not what it does — the label says that. */
  hint?: ReactNode;
  /** Makes the label click through to the control, where there is one. */
  htmlFor?: string;
  control: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-nf-ds-2 sm:flex-row sm:items-center sm:justify-between sm:gap-nf-ds-6">
      <span className="flex min-w-0 flex-col gap-nf-ds-1">
        <Label htmlFor={htmlFor}>{label}</Label>
        {hint ? (
          <Hint as="span" muted className="text-pretty">
            {hint}
          </Hint>
        ) : null}
      </span>
      <div className={SETTING_CONTROL}>{control}</div>
    </div>
  );
}
